import { get, post } from 'data/services/apiService';
import { getApiUrl } from 'data/services/apiConstants';
import { closeModal, openModal } from 'data/reducers/modalReducer';
import { showToast } from 'data/reducers/toastReducer';
import { MODAL_TYPES } from 'view/ModalManager';

// Action Types
export const FETCH_CURRENT_SUBSCRIPTION_REQUEST =
	'FETCH_CURRENT_SUBSCRIPTION_REQUEST';
export const FETCH_CURRENT_SUBSCRIPTION_SUCCESS =
	'FETCH_CURRENT_SUBSCRIPTION_SUCCESS';
export const FETCH_CURRENT_SUBSCRIPTION_ERROR =
	'FETCH_CURRENT_SUBSCRIPTION_ERROR';

export const FETCH_PAYMENT_HISTORY_REQUEST = 'FETCH_PAYMENT_HISTORY_REQUEST';
export const FETCH_PAYMENT_HISTORY_SUCCESS = 'FETCH_PAYMENT_HISTORY_SUCCESS';
export const FETCH_PAYMENT_HISTORY_ERROR = 'FETCH_PAYMENT_HISTORY_ERROR';

export const FETCH_SUBSCRIPTION_HISTORY_REQUEST =
	'FETCH_SUBSCRIPTION_HISTORY_REQUEST';
export const FETCH_SUBSCRIPTION_HISTORY_SUCCESS =
	'FETCH_SUBSCRIPTION_HISTORY_SUCCESS';
export const FETCH_SUBSCRIPTION_HISTORY_ERROR =
	'FETCH_SUBSCRIPTION_HISTORY_ERROR';

export const UPGRADE_SUBSCRIPTION_REQUEST = 'UPGRADE_SUBSCRIPTION_REQUEST';
export const UPGRADE_SUBSCRIPTION_SUCCESS = 'UPGRADE_SUBSCRIPTION_SUCCESS';
export const UPGRADE_SUBSCRIPTION_ERROR = 'UPGRADE_SUBSCRIPTION_ERROR';

export const DOWNGRADE_SUBSCRIPTION_REQUEST = 'DOWNGRADE_SUBSCRIPTION_REQUEST';
export const DOWNGRADE_SUBSCRIPTION_SUCCESS = 'DOWNGRADE_SUBSCRIPTION_SUCCESS';
export const DOWNGRADE_SUBSCRIPTION_ERROR = 'DOWNGRADE_SUBSCRIPTION_ERROR';

export const PAUSE_SUBSCRIPTION_REQUEST = 'PAUSE_SUBSCRIPTION_REQUEST';
export const PAUSE_SUBSCRIPTION_SUCCESS = 'PAUSE_SUBSCRIPTION_SUCCESS';
export const PAUSE_SUBSCRIPTION_ERROR = 'PAUSE_SUBSCRIPTION_ERROR';

export const RESUME_SUBSCRIPTION_REQUEST = 'RESUME_SUBSCRIPTION_REQUEST';
export const RESUME_SUBSCRIPTION_SUCCESS = 'RESUME_SUBSCRIPTION_SUCCESS';
export const RESUME_SUBSCRIPTION_ERROR = 'RESUME_SUBSCRIPTION_ERROR';

export const CANCEL_SUBSCRIPTION_REQUEST = 'CANCEL_SUBSCRIPTION_REQUEST';
export const CANCEL_SUBSCRIPTION_SUCCESS = 'CANCEL_SUBSCRIPTION_SUCCESS';
export const CANCEL_SUBSCRIPTION_ERROR = 'CANCEL_SUBSCRIPTION_ERROR';

export const FETCH_AVAILABLE_PLANS_REQUEST = 'FETCH_AVAILABLE_PLANS_REQUEST';
export const FETCH_AVAILABLE_PLANS_SUCCESS = 'FETCH_AVAILABLE_PLANS_SUCCESS';
export const FETCH_AVAILABLE_PLANS_ERROR = 'FETCH_AVAILABLE_PLANS_ERROR';

export const CLEAR_SUBSCRIPTION_ERRORS = 'CLEAR_SUBSCRIPTION_ERRORS';

// New Stripe Checkout Action Types
export const CREATE_UPGRADE_CHECKOUT_REQUEST =
	'CREATE_UPGRADE_CHECKOUT_REQUEST';
export const CREATE_UPGRADE_CHECKOUT_SUCCESS =
	'CREATE_UPGRADE_CHECKOUT_SUCCESS';
export const CREATE_UPGRADE_CHECKOUT_ERROR = 'CREATE_UPGRADE_CHECKOUT_ERROR';

export const CREATE_DOWNGRADE_CHECKOUT_REQUEST =
	'CREATE_DOWNGRADE_CHECKOUT_REQUEST';
export const CREATE_DOWNGRADE_CHECKOUT_SUCCESS =
	'CREATE_DOWNGRADE_CHECKOUT_SUCCESS';
export const CREATE_DOWNGRADE_CHECKOUT_ERROR =
	'CREATE_DOWNGRADE_CHECKOUT_ERROR';

export const CREATE_PORTAL_SESSION_REQUEST = 'CREATE_PORTAL_SESSION_REQUEST';
export const CREATE_PORTAL_SESSION_SUCCESS = 'CREATE_PORTAL_SESSION_SUCCESS';
export const CREATE_PORTAL_SESSION_ERROR = 'CREATE_PORTAL_SESSION_ERROR';

export const CREATE_SUBSCRIBE_CHECKOUT_REQUEST =
	'CREATE_SUBSCRIBE_CHECKOUT_REQUEST';
export const CREATE_SUBSCRIBE_CHECKOUT_SUCCESS =
	'CREATE_SUBSCRIBE_CHECKOUT_SUCCESS';
export const CREATE_SUBSCRIBE_CHECKOUT_ERROR =
	'CREATE_SUBSCRIBE_CHECKOUT_ERROR';

// Usage Summary Action Types
export const FETCH_USAGE_SUMMARY_REQUEST = 'FETCH_USAGE_SUMMARY_REQUEST';
export const FETCH_USAGE_SUMMARY_SUCCESS = 'FETCH_USAGE_SUMMARY_SUCCESS';
export const FETCH_USAGE_SUMMARY_ERROR = 'FETCH_USAGE_SUMMARY_ERROR';

// SMS Add-on Action Types
export const UPDATE_SMS_ADDON_REQUEST = 'UPDATE_SMS_ADDON_REQUEST';
export const UPDATE_SMS_ADDON_SUCCESS = 'UPDATE_SMS_ADDON_SUCCESS';
export const UPDATE_SMS_ADDON_ERROR = 'UPDATE_SMS_ADDON_ERROR';

// Cancel Flow Action Types
export const CHECK_CANCEL_LIMITS_REQUEST = 'CHECK_CANCEL_LIMITS_REQUEST';
export const CHECK_CANCEL_LIMITS_SUCCESS = 'CHECK_CANCEL_LIMITS_SUCCESS';
export const CHECK_CANCEL_LIMITS_ERROR = 'CHECK_CANCEL_LIMITS_ERROR';

// Initial State
export const initialState = {
	// Current subscription data
	currentSubscription: null,
	hasSubscription: false,
	subscriptionStatus: 'none',
	isLoadingCurrentSubscription: true,
	currentSubscriptionError: null,

	// Payment history
	paymentHistory: {
		data: [],
		currentPage: 1,
		lastPage: 1,
		perPage: 15,
		total: 0,
		from: null,
		to: null,
	},
	isLoadingPaymentHistory: false,
	paymentHistoryError: null,

	// Subscription history
	subscriptionHistory: null,
	isLoadingSubscriptionHistory: false,
	subscriptionHistoryError: null,

	// Available plans
	availablePlans: [],
	isLoadingAvailablePlans: false,
	availablePlansError: null,

	// Subscription management states
	isUpgrading: false,
	upgradeError: null,

	isDowngrading: false,
	downgradeError: null,

	isPausing: false,
	pauseError: null,

	isResuming: false,
	resumeError: null,

	isCancelling: false,
	cancelError: null,

	// General loading state for any subscription operation
	isPerformingAction: false,

	// Cancel flow states
	isCheckingLimits: false,
	limitsCheckError: null,
	cancelFlowData: null,

	// Usage Summary states
	usageSummary: null,
	isLoadingUsageSummary: false,
	usageSummaryError: null,

	// Stripe Checkout states
	isCreatingUpgradeCheckout: false,
	upgradeCheckoutError: null,

	isCreatingDowngradeCheckout: false,
	downgradeCheckoutError: null,

	isCreatingPortalSession: false,
	portalSessionError: null,

	isCreatingSubscribeCheckout: false,
	subscribeCheckoutError: null,

	// SMS Add-on states
	isUpdatingSmsAddon: false,
	smsAddonError: null,
};

// Action Creators - Current Subscription
export const fetchCurrentSubscriptionRequest = () => ({
	type: FETCH_CURRENT_SUBSCRIPTION_REQUEST,
});

export const fetchCurrentSubscriptionSuccess = (data) => ({
	type: FETCH_CURRENT_SUBSCRIPTION_SUCCESS,
	payload: data,
});

export const fetchCurrentSubscriptionError = (error) => ({
	type: FETCH_CURRENT_SUBSCRIPTION_ERROR,
	payload: error,
});

// Action Creators - Payment History
export const fetchPaymentHistoryRequest = () => ({
	type: FETCH_PAYMENT_HISTORY_REQUEST,
});

export const fetchPaymentHistorySuccess = (data) => ({
	type: FETCH_PAYMENT_HISTORY_SUCCESS,
	payload: data,
});

export const fetchPaymentHistoryError = (error) => ({
	type: FETCH_PAYMENT_HISTORY_ERROR,
	payload: error,
});

// Action Creators - Subscription History
export const fetchSubscriptionHistoryRequest = () => ({
	type: FETCH_SUBSCRIPTION_HISTORY_REQUEST,
});

export const fetchSubscriptionHistorySuccess = (data) => ({
	type: FETCH_SUBSCRIPTION_HISTORY_SUCCESS,
	payload: data,
});

export const fetchSubscriptionHistoryError = (error) => ({
	type: FETCH_SUBSCRIPTION_HISTORY_ERROR,
	payload: error,
});

// Action Creators - Available Plans
export const fetchAvailablePlansRequest = () => ({
	type: FETCH_AVAILABLE_PLANS_REQUEST,
});

export const fetchAvailablePlansSuccess = (plans) => ({
	type: FETCH_AVAILABLE_PLANS_SUCCESS,
	payload: plans,
});

export const fetchAvailablePlansError = (error) => ({
	type: FETCH_AVAILABLE_PLANS_ERROR,
	payload: error,
});

// Action Creators - Subscription Management
export const upgradeSubscriptionRequest = () => ({
	type: UPGRADE_SUBSCRIPTION_REQUEST,
});

export const upgradeSubscriptionSuccess = (data) => ({
	type: UPGRADE_SUBSCRIPTION_SUCCESS,
	payload: data,
});

export const upgradeSubscriptionError = (error) => ({
	type: UPGRADE_SUBSCRIPTION_ERROR,
	payload: error,
});

export const downgradeSubscriptionRequest = () => ({
	type: DOWNGRADE_SUBSCRIPTION_REQUEST,
});

export const downgradeSubscriptionSuccess = (data) => ({
	type: DOWNGRADE_SUBSCRIPTION_SUCCESS,
	payload: data,
});

export const downgradeSubscriptionError = (error) => ({
	type: DOWNGRADE_SUBSCRIPTION_ERROR,
	payload: error,
});

export const pauseSubscriptionRequest = () => ({
	type: PAUSE_SUBSCRIPTION_REQUEST,
});

export const pauseSubscriptionSuccess = (data) => ({
	type: PAUSE_SUBSCRIPTION_SUCCESS,
	payload: data,
});

export const pauseSubscriptionError = (error) => ({
	type: PAUSE_SUBSCRIPTION_ERROR,
	payload: error,
});

export const resumeSubscriptionRequest = () => ({
	type: RESUME_SUBSCRIPTION_REQUEST,
});

export const resumeSubscriptionSuccess = (data) => ({
	type: RESUME_SUBSCRIPTION_SUCCESS,
	payload: data,
});

export const resumeSubscriptionError = (error) => ({
	type: RESUME_SUBSCRIPTION_ERROR,
	payload: error,
});

export const cancelSubscriptionRequest = () => ({
	type: CANCEL_SUBSCRIPTION_REQUEST,
});

export const cancelSubscriptionSuccess = (data) => ({
	type: CANCEL_SUBSCRIPTION_SUCCESS,
	payload: data,
});

export const cancelSubscriptionError = (error) => ({
	type: CANCEL_SUBSCRIPTION_ERROR,
	payload: error,
});

export const clearSubscriptionErrors = () => ({
	type: CLEAR_SUBSCRIPTION_ERRORS,
});

// Action Creators - Stripe Checkout
export const createUpgradeCheckoutRequest = () => ({
	type: CREATE_UPGRADE_CHECKOUT_REQUEST,
});

export const createUpgradeCheckoutSuccess = (data) => ({
	type: CREATE_UPGRADE_CHECKOUT_SUCCESS,
	payload: data,
});

export const createUpgradeCheckoutError = (error) => ({
	type: CREATE_UPGRADE_CHECKOUT_ERROR,
	payload: error,
});

export const createDowngradeCheckoutRequest = () => ({
	type: CREATE_DOWNGRADE_CHECKOUT_REQUEST,
});

export const createDowngradeCheckoutSuccess = (data) => ({
	type: CREATE_DOWNGRADE_CHECKOUT_SUCCESS,
	payload: data,
});

export const createDowngradeCheckoutError = (error) => ({
	type: CREATE_DOWNGRADE_CHECKOUT_ERROR,
	payload: error,
});

export const createPortalSessionRequest = () => ({
	type: CREATE_PORTAL_SESSION_REQUEST,
});

export const createPortalSessionSuccess = (data) => ({
	type: CREATE_PORTAL_SESSION_SUCCESS,
	payload: data,
});

export const createPortalSessionError = (error) => ({
	type: CREATE_PORTAL_SESSION_ERROR,
	payload: error,
});

export const createSubscribeCheckoutRequest = () => ({
	type: CREATE_SUBSCRIBE_CHECKOUT_REQUEST,
});

export const createSubscribeCheckoutSuccess = (data) => ({
	type: CREATE_SUBSCRIBE_CHECKOUT_SUCCESS,
	payload: data,
});

export const createSubscribeCheckoutError = (error) => ({
	type: CREATE_SUBSCRIBE_CHECKOUT_ERROR,
	payload: error,
});

// Usage Summary Action Creators
export const fetchUsageSummaryRequest = () => ({
	type: FETCH_USAGE_SUMMARY_REQUEST,
});

export const fetchUsageSummarySuccess = (data) => ({
	type: FETCH_USAGE_SUMMARY_SUCCESS,
	payload: data,
});

export const fetchUsageSummaryError = (error) => ({
	type: FETCH_USAGE_SUMMARY_ERROR,
	payload: error,
});

// Cancel Flow Action Creators
export const checkCancelLimitsRequest = () => ({
	type: CHECK_CANCEL_LIMITS_REQUEST,
});

export const checkCancelLimitsSuccess = (data) => ({
	type: CHECK_CANCEL_LIMITS_SUCCESS,
	payload: data,
});

export const checkCancelLimitsError = (error) => ({
	type: CHECK_CANCEL_LIMITS_ERROR,
	payload: error,
});

// Thunk Actions
export const fetchCurrentSubscription = () => (dispatch, getState) => {
	dispatch(fetchCurrentSubscriptionRequest());

	const { user } = getState();
	const authToken = user.authToken;

	return get({
		url: `${getApiUrl()}/api/subscriptions/current`,
		authToken,
		dispatch,
		onSuccess: (response) => {
			// The response might be nested differently
			const data = response.data || response;
			dispatch(fetchCurrentSubscriptionSuccess(data));
		},
		onError: (error) => {
			dispatch(
				fetchCurrentSubscriptionError(
					error.message || 'Failed to fetch current subscription',
				),
			);
		},
	});
};

export const fetchPaymentHistory =
	(page = 1, perPage = 15) =>
	(dispatch, getState) => {
		dispatch(fetchPaymentHistoryRequest());

		const { user } = getState();
		const authToken = user.authToken;

		return get({
			url: `${getApiUrl()}/api/subscriptions/payments?page=${page}&per_page=${perPage}`,
			authToken,
			dispatch,
			onSuccess: (response) => {
				dispatch(fetchPaymentHistorySuccess(response));
			},
			onError: (error) => {
				dispatch(
					fetchPaymentHistoryError(
						error.message || 'Failed to fetch payment history',
					),
				);
			},
		});
	};

// Thunk Actions - Subscription History
export const fetchSubscriptionHistory =
	(page = 1, perPage = 15) =>
	(dispatch, getState) => {
		dispatch(fetchSubscriptionHistoryRequest());

		const { user } = getState();
		const authToken = user.authToken;

		return get({
			url: `${getApiUrl()}/api/subscriptions/history?page=${page}&per_page=${perPage}`,
			authToken,
			dispatch,
			onSuccess: (response) => {
				dispatch(fetchSubscriptionHistorySuccess(response));
			},
			onError: (error) => {
				dispatch(
					fetchSubscriptionHistoryError(
						error.message || 'Failed to fetch subscription history',
					),
				);
			},
		});
	};

export const fetchAvailablePlans = () => (dispatch, getState) => {
	dispatch(fetchAvailablePlansRequest());

	const { user } = getState();
	const authToken = user.authToken;

	// Subscription plans should be publicly available for pricing pages
	// Pass authToken if available, but don't require it
	return get({
		url: `${getApiUrl()}/api/subscription-plans`,
		authToken: authToken || null, // Pass null instead of undefined if no token
		dispatch,
		onSuccess: (response) => {
			dispatch(fetchAvailablePlansSuccess(response.data));
		},
		onError: (error) => {
			dispatch(
				fetchAvailablePlansError(
					error.message || 'Failed to fetch available plans',
				),
			);
		},
	});
};

export const upgradeSubscription =
	(planId, prorate = true) =>
	(dispatch, getState) => {
		dispatch(upgradeSubscriptionRequest());

		const { user } = getState();
		const authToken = user.authToken;

		return post({
			url: `${getApiUrl()}/api/subscriptions/upgrade`,
			data: { plan_id: planId, prorate },
			authToken,
			dispatch,
			onSuccess: (response) => {
				dispatch(upgradeSubscriptionSuccess(response.data));
				// Refresh current subscription data
				dispatch(fetchCurrentSubscription());
				dispatch(
					showToast({
						// type: 'success',
						title: 'Success!',
						body: 'Subscription upgraded successfully.',
						duration: 1000,
					}),
				);
			},
			onError: (error) => {
				dispatch(
					upgradeSubscriptionError(
						error.message || 'Failed to upgrade subscription',
					),
				);
			},
		});
	};

export const downgradeSubscription =
	(planId, immediate = false) =>
	(dispatch, getState) => {
		dispatch(downgradeSubscriptionRequest());

		const { user } = getState();
		const authToken = user.authToken;

		return post({
			url: `${getApiUrl()}/api/subscriptions/downgrade`,
			data: { plan_id: planId, immediate },
			authToken,
			dispatch,
			onSuccess: (response) => {
				dispatch(downgradeSubscriptionSuccess(response.data));
				// Refresh current subscription data
				dispatch(fetchCurrentSubscription());

				dispatch(
					showToast({
						// type: 'success',
						title: 'Success!',
						body: 'Downgrade successful.',
						duration: 1000,
					}),
				);

				dispatch(closeModal());
			},
			onError: (error) => {
				dispatch(
					downgradeSubscriptionError(
						error.message || 'Failed to downgrade subscription',
					),
				);
			},
		});
	};

export const pauseSubscription = () => (dispatch, getState) => {
	dispatch(pauseSubscriptionRequest());

	const { user } = getState();
	const authToken = user.authToken;

	return post({
		url: `${getApiUrl()}/api/subscriptions/pause`,
		authToken,
		dispatch,
		onSuccess: (response) => {
			dispatch(pauseSubscriptionSuccess(response.data));
			// Refresh current subscription data
			dispatch(fetchCurrentSubscription());
			dispatch(
				showToast({
					// type: 'success',
					title: 'Success!',
					body: 'Subscription paused successfully.',
					duration: 1000,
				}),
			);
			dispatch(closeModal());
		},
		onError: (error) => {
			dispatch(
				pauseSubscriptionError(error.message || 'Failed to pause subscription'),
			);
		},
	});
};

export const resumeSubscription = () => (dispatch, getState) => {
	dispatch(resumeSubscriptionRequest());

	const { user } = getState();
	const authToken = user.authToken;

	return post({
		url: `${getApiUrl()}/api/subscriptions/resume`,
		authToken,
		dispatch,
		onSuccess: (response) => {
			dispatch(resumeSubscriptionSuccess(response.data));
			// Refresh current subscription data
			dispatch(fetchCurrentSubscription());
			dispatch(
				showToast({
					// type: 'success',
					title: 'Success!',
					body: 'Subscription resumed successfully.',
					duration: 1000,
				}),
			);
			dispatch(closeModal());
		},
		onError: (error) => {
			dispatch(
				resumeSubscriptionError(
					error.message || 'Failed to resume subscription',
				),
			);
		},
	});
};

export const cancelSubscription =
	(immediately = false, reason = null) =>
	(dispatch, getState) => {
		dispatch(cancelSubscriptionRequest());

		const { user } = getState();
		const authToken = user.authToken;

		return post({
			url: `${getApiUrl()}/api/subscriptions/cancel`,
			data: { immediately, reason },
			authToken,
			dispatch,
			onSuccess: (response) => {
				dispatch(cancelSubscriptionSuccess(response.data));
				// Refresh current subscription data

				setTimeout(() => {
					// dispatch(fetchCurrentSubscription());
					window.location.reload();
				}, 700); // Slight delay to allow backend to process

				dispatch(
					showToast({
						// type: 'success',
						title: 'Success!',
						body: 'Subscription cancelled successfully.',
						duration: 1000,
					}),
				);
				dispatch(closeModal());
			},
			onError: (error) => {
				dispatch(
					cancelSubscriptionError(
						error.message || 'Failed to cancel subscription',
					),
				);
			},
		});
	};

export const createSubscribeCheckout =
	(planId, withSmsAddon = false) =>
	(dispatch, getState) => {
		dispatch(createSubscribeCheckoutRequest());

		const { user } = getState();
		const authToken = user.authToken;

		return post({
			url: `${getApiUrl()}/api/subscriptions/subscribe-checkout`,
			data: { plan_id: planId, with_sms_addon: withSmsAddon },
			authToken,
			dispatch,
			onSuccess: (response) => {
				dispatch(createSubscribeCheckoutSuccess(response));
				// Redirect to Stripe Checkout
				if (response.checkoutUrl) {
					window.location.href = response.checkoutUrl;
				}
			},
			onError: (error) => {
				dispatch(
					createSubscribeCheckoutError(
						error.message || 'Failed to create subscribe checkout session',
					),
				);
			},
		});
	};

export const createSubscribeCheckoutWithTrial =
	(planId, trialDays, trialSource = 'organic', withSmsAddon = false) =>
	(dispatch, getState) => {
		dispatch(createSubscribeCheckoutRequest());

		const { user } = getState();
		const authToken = user.authToken;

		return post({
			url: `${getApiUrl()}/api/subscriptions/subscribe-checkout`,
			data: {
				plan_id: planId,
				trial_days: trialDays,
				trial_source: trialSource,
				with_sms_addon: withSmsAddon,
			},
			authToken,
			dispatch,
			onSuccess: (response) => {
				dispatch(createSubscribeCheckoutSuccess(response));
				// Redirect to Stripe Checkout
				if (response.checkoutUrl) {
					window.location.href = response.checkoutUrl;
				}
			},
			onError: (error) => {
				dispatch(
					createSubscribeCheckoutError(
						error.message || 'Failed to create trial checkout session',
					),
				);
			},
		});
	};
// Usage Summary Thunk Action
export const fetchUsageSummary = () => (dispatch, getState) => {
	dispatch(fetchUsageSummaryRequest());

	const { user } = getState();
	const authToken = user.authToken;

	return get({
		url: `${getApiUrl()}/api/subscriptions/usage-summary`,
		authToken,
		dispatch,
		onSuccess: (response) => {
			const usageData = response.data || response;
			dispatch(fetchUsageSummarySuccess(usageData));
		},
		onError: (error) => {
			dispatch(
				fetchUsageSummaryError(
					error.message || 'Failed to fetch usage summary',
				),
			);
		},
	});
};

// Carried as targetPlanInfo.smsAddon while a downgrade travels through the
// limits check and the confirm modal. Null means the user changed nothing on
// the add-on card, so the stored flag is left exactly as it is.
export const NO_SMS_ADDON_CHANGE = null;

// SMS Add-on Action Creators & Thunk
export const updateSmsAddonRequest = () => ({
	type: UPDATE_SMS_ADDON_REQUEST,
});

export const updateSmsAddonSuccess = (data) => ({
	type: UPDATE_SMS_ADDON_SUCCESS,
	payload: data,
});

export const updateSmsAddonError = (error) => ({
	type: UPDATE_SMS_ADDON_ERROR,
	payload: error,
});

export const updateSmsAddon = (enabled) => (dispatch, getState) => {
	dispatch(updateSmsAddonRequest());

	const { user } = getState();
	const authToken = user.authToken;

	return post({
		url: `${getApiUrl()}/api/subscriptions/sms-addon`,
		data: { enabled },
		authToken,
		dispatch,
		onSuccess: (response) => {
			dispatch(updateSmsAddonSuccess(response));
			dispatch(fetchCurrentSubscription());
			dispatch(fetchUsageSummary());
			dispatch(
				showToast({
					title: 'Success!',
					body: enabled
						? 'SMS Alerts Add-on enabled ($19.99/mo).'
						: 'SMS Alerts Add-on disabled.',
					duration: 2000,
				}),
			);
		},
		onError: (error) => {
			dispatch(
				updateSmsAddonError(
					error.message || 'Failed to update SMS Alerts Add-on',
				),
			);
			dispatch(
				showToast({
					title: 'Error',
					body: error.message || 'Failed to update SMS Alerts Add-on',
					duration: 2500,
				}),
			);
		},
	});
};

// Generic function to check plan limits for both cancel and downgrade
export const checkPlanLimits =
	(targetLimits, actionType, targetPlanInfo = null) =>
	(dispatch, getState) => {
		dispatch(checkCancelLimitsRequest());

		const { user } = getState();
		const authToken = user.authToken;

		return get({
			url: `${getApiUrl()}/api/subscriptions/usage-summary`,
			authToken,
			dispatch,
			onSuccess: (response) => {
				const usageData = response.data || response;
				dispatch(checkCancelLimitsSuccess(usageData));

				const { usage } = usageData;

				const exceedsPropertyLimit =
					usage.properties >
					(targetLimits.properties === -1 ? 1000000 : targetLimits.properties);
				const exceedsSeatsLimit =
					usage.seats >
					(targetLimits.seats === -1 ? 1000000 : targetLimits.seats);

				if (exceedsPropertyLimit || exceedsSeatsLimit) {
					// User needs to remove properties first
					dispatch(
						openModal(MODAL_TYPES.SUBSCRIPTION_EXCEED_LIMIT, {
							action: exceedsPropertyLimit
								? `${actionType}_manage_properties`
								: `${actionType}_manage_users`,
							limitsData: usageData,
							targetLimits,
							actionType,
							targetPlanInfo,
						}),
					);
				} else {
					// User is within limits, proceed to next action
					dispatch(
						proceedAfterLimitsCheck(actionType, usageData, targetPlanInfo),
					);
				}
			},
			onError: (error) => {
				dispatch(
					checkCancelLimitsError(
						error.message || 'Failed to check usage limits',
					),
				);
			},
		});
	};

// Cancel Flow Thunk Actions
export const checkCancelLimits = () => (dispatch, getState) => {
	const freeLimits = { properties: 30, seats: 3 }; // Free plan limits
	return dispatch(checkPlanLimits(freeLimits, 'cancel'));
};

// Downgrade Flow Thunk Actions
/**
 * Start a downgrade by checking the target plan's limits, carrying the add-on
 * selection the user made on the downgrade screen so it survives the modal
 * replacements between that screen and the confirm step.
 */
export const checkDowngradeLimits =
	(targetPlanId, targetPlanName, smsAddon = NO_SMS_ADDON_CHANGE) =>
	(dispatch, getState) => {
		// Define plan limits - you may want to fetch these from the API or store them in constants
		const planLimits = {
			starter: { properties: 75, seats: 10 },
			prop: { properties: 120, seats: 35 },
			business: { properties: 200, seats: 99999 },
			free: { properties: 30, seats: 3 },
		};

		// Extract plan type from plan ID or name
		const planType = targetPlanName?.toLowerCase().includes('starter')
			? 'starter'
			: targetPlanName?.toLowerCase().includes('basic')
				? 'basic'
				: targetPlanName?.toLowerCase().includes('pro')
					? 'pro'
					: 'free';

		const targetLimits = planLimits[planType] || planLimits.free;
		const targetPlanInfo = {
			id: targetPlanId,
			name: targetPlanName,
			type: planType,
			smsAddon,
		};

		return dispatch(checkPlanLimits(targetLimits, 'downgrade', targetPlanInfo));
	};

// Proceed with the appropriate action after limits check
const proceedAfterLimitsCheck =
	(actionType, usageData, targetPlanInfo) => (dispatch, getState) => {
		if (actionType === 'cancel') {
			// For cancel, proceed to cancellation reason
			dispatch(
				openModal(MODAL_TYPES.SUBSCRIPTION_ACTION, {
					action: 'cancel',
					limitsData: usageData,
				}),
			);
		} else if (actionType === 'downgrade') {
			// For downgrade, show confirm modal then perform downgrade
			dispatch(performDowngradeAfterLimitsCheck(targetPlanInfo));
		}
	};

/**
 * Confirm and perform a downgrade once usage is within the target plan limits,
 * applying any add-on selection carried on targetPlanInfo after the plan
 * change resolves.
 */
export const performDowngradeAfterLimitsCheck =
	(targetPlanInfo) => (dispatch, getState) => {
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				title: `Confirm Downgrade to ${targetPlanInfo?.name}`,
				message: `You have successfully reduced your usage to meet the ${targetPlanInfo?.name} plan limits. Do you want to proceed with the downgrade?`,
				confirmLabel: 'Confirm Downgrade',
				closeLabel: 'Cancel',
				color: 'primary',
				onConfirm: async () => {
					try {
						const result = await dispatch(
							downgradeSubscription(targetPlanInfo?.id),
						);

						// The downgrade request resolved, so act on the add-on the
						// user selected before the confirm modal replaced their
						// screen. This keys on the resolved request rather than on
						// the response-type test below, which the response body does
						// not carry.
						const smsAddon = targetPlanInfo?.smsAddon;
						if (typeof smsAddon === 'boolean') {
							try {
								await dispatch(updateSmsAddon(smsAddon));
							} catch (smsAddonFailure) {
								// The plan change already went through, so an add-on
								// failure must not be reported as a failed downgrade;
								// updateSmsAddon has already surfaced it to the user.
								console.error(
									'SMS add-on update failed after downgrade:',
									smsAddonFailure,
								);
							}
						}

						if (result?.type?.includes('SUCCESS')) {
							dispatch(
								showToast({
									type: 'success',
									title: 'Success!',
									message: `Successfully downgraded to ${targetPlanInfo?.name} plan!`,
									duration: 4000,
								}),
							);
							// Refresh the page after downgrade
							setTimeout(() => {
								window.location.reload();
							}, 1000);
						}
					} catch (error) {
						console.error('Downgrade failed:', error);
						dispatch(
							showToast({
								type: 'error',
								title: 'Error',
								message: 'Failed to downgrade plan. Please try again.',
								duration: 5000,
							}),
						);
					}
				},
			}),
		);
	};

// Reducer
const subscriptionReducer = (state = initialState, action) => {
	switch (action.type) {
		// Current Subscription
		case FETCH_CURRENT_SUBSCRIPTION_REQUEST:
			return {
				...state,
				isLoadingCurrentSubscription: true,
				currentSubscriptionError: null,
			};

		case FETCH_CURRENT_SUBSCRIPTION_SUCCESS:
			// Handle different response structures
			const responseData = action.payload.data || action.payload;
			return {
				...state,
				isLoadingCurrentSubscription: false,
				currentSubscription: responseData.subscription || responseData,
				hasSubscription: responseData.has_subscription || false,
				subscriptionStatus: responseData.status || 'none',
				currentSubscriptionError: null,
			};

		case FETCH_CURRENT_SUBSCRIPTION_ERROR:
			return {
				...state,
				isLoadingCurrentSubscription: false,
				currentSubscriptionError: action.payload,
			};

		// Payment History
		case FETCH_PAYMENT_HISTORY_REQUEST:
			return {
				...state,
				isLoadingPaymentHistory: true,
				paymentHistoryError: null,
			};

		case FETCH_PAYMENT_HISTORY_SUCCESS:
			// Transform the API response to match the expected structure
			const paymentData = action.payload;
			return {
				...state,
				isLoadingPaymentHistory: false,
				paymentHistory: {
					data: paymentData.data || [],
					currentPage: paymentData.current_page || 1,
					lastPage: paymentData.last_page || 1,
					perPage: paymentData.per_page || 15,
					total: paymentData.total || 0,
					from: paymentData.from || null,
					to: paymentData.to || null,
				},
				paymentHistoryError: null,
			};

		case FETCH_PAYMENT_HISTORY_ERROR:
			return {
				...state,
				isLoadingPaymentHistory: false,
				paymentHistoryError: action.payload,
			};

		// Subscription History
		case FETCH_SUBSCRIPTION_HISTORY_REQUEST:
			return {
				...state,
				isLoadingSubscriptionHistory: true,
				subscriptionHistoryError: null,
			};

		case FETCH_SUBSCRIPTION_HISTORY_SUCCESS:
			return {
				...state,
				isLoadingSubscriptionHistory: false,
				subscriptionHistory: action.payload,
				subscriptionHistoryError: null,
			};

		case FETCH_SUBSCRIPTION_HISTORY_ERROR:
			return {
				...state,
				isLoadingSubscriptionHistory: false,
				subscriptionHistoryError: action.payload,
			};

		// Available Plans
		case FETCH_AVAILABLE_PLANS_REQUEST:
			return {
				...state,
				isLoadingAvailablePlans: true,
				availablePlansError: null,
			};

		case FETCH_AVAILABLE_PLANS_SUCCESS:
			return {
				...state,
				isLoadingAvailablePlans: false,
				availablePlans: action.payload,
				availablePlansError: null,
			};

		case FETCH_AVAILABLE_PLANS_ERROR:
			return {
				...state,
				isLoadingAvailablePlans: false,
				availablePlansError: action.payload,
			};

		// Upgrade Subscription
		case UPGRADE_SUBSCRIPTION_REQUEST:
			return {
				...state,
				isUpgrading: true,
				isPerformingAction: true,
				upgradeError: null,
				upgradeSuccessMessage: null,
			};

		case UPGRADE_SUBSCRIPTION_SUCCESS:
			return {
				...state,
				isUpgrading: false,
				isPerformingAction: false,
				upgradeError: null,
				upgradeSuccessMessage: 'Subscription upgraded successfully.',
			};

		case UPGRADE_SUBSCRIPTION_ERROR:
			return {
				...state,
				isUpgrading: false,
				isPerformingAction: false,
				upgradeError: action.payload,
				upgradeSuccessMessage: null,
			};

		// Downgrade Subscription
		case DOWNGRADE_SUBSCRIPTION_REQUEST:
			return {
				...state,
				isDowngrading: true,
				isPerformingAction: true,
				downgradeError: null,
				downgradeSuccessMessage: null,
			};

		case DOWNGRADE_SUBSCRIPTION_SUCCESS:
			return {
				...state,
				isDowngrading: false,
				isPerformingAction: false,
				downgradeError: null,
				downgradeSuccessMessage:
					'Downgrade successful. Changes will apply after current period.',
			};

		case DOWNGRADE_SUBSCRIPTION_ERROR:
			return {
				...state,
				isDowngrading: false,
				isPerformingAction: false,
				downgradeError: action.payload,
				downgradeSuccessMessage: null,
			};

		// Pause Subscription
		case PAUSE_SUBSCRIPTION_REQUEST:
			return {
				...state,
				isPausing: true,
				isPerformingAction: true,
				pauseError: null,
				pauseSuccessMessage: null,
			};

		case PAUSE_SUBSCRIPTION_SUCCESS:
			return {
				...state,
				isPausing: false,
				isPerformingAction: false,
				pauseError: null,
				pauseSuccessMessage: 'Subscription paused successfully.',
			};

		case PAUSE_SUBSCRIPTION_ERROR:
			return {
				...state,
				isPausing: false,
				isPerformingAction: false,
				pauseError: action.payload,
				pauseSuccessMessage: null,
			};

		// Resume Subscription
		case RESUME_SUBSCRIPTION_REQUEST:
			return {
				...state,
				isResuming: true,
				isPerformingAction: true,
				resumeError: null,
				resumeSuccessMessage: null,
			};

		case RESUME_SUBSCRIPTION_SUCCESS:
			return {
				...state,
				isResuming: false,
				isPerformingAction: false,
				resumeError: null,
				resumeSuccessMessage: 'Subscription resumed successfully.',
			};

		case RESUME_SUBSCRIPTION_ERROR:
			return {
				...state,
				isResuming: false,
				isPerformingAction: false,
				resumeError: action.payload,
				resumeSuccessMessage: null,
			};

		// Cancel Subscription
		case CANCEL_SUBSCRIPTION_REQUEST:
			return {
				...state,
				isCancelling: true,
				isPerformingAction: true,
				cancelError: null,
			};

		case CANCEL_SUBSCRIPTION_SUCCESS:
			return {
				...state,
				isCancelling: false,
				isPerformingAction: false,
				cancelError: null,
			};

		case CANCEL_SUBSCRIPTION_ERROR:
			return {
				...state,
				isCancelling: false,
				isPerformingAction: false,
				cancelError: action.payload,
			};

		// Stripe Checkout - Upgrade
		case CREATE_UPGRADE_CHECKOUT_REQUEST:
			return {
				...state,
				isCreatingUpgradeCheckout: true,
				isPerformingAction: true,
				upgradeCheckoutError: null,
			};

		case CREATE_UPGRADE_CHECKOUT_SUCCESS:
			return {
				...state,
				isCreatingUpgradeCheckout: false,
				isPerformingAction: false,
				upgradeCheckoutError: null,
			};

		case CREATE_UPGRADE_CHECKOUT_ERROR:
			return {
				...state,
				isCreatingUpgradeCheckout: false,
				isPerformingAction: false,
				upgradeCheckoutError: action.payload,
			};

		// Stripe Checkout - Downgrade
		case CREATE_DOWNGRADE_CHECKOUT_REQUEST:
			return {
				...state,
				isCreatingDowngradeCheckout: true,
				isPerformingAction: true,
				downgradeCheckoutError: null,
			};

		case CREATE_DOWNGRADE_CHECKOUT_SUCCESS:
			return {
				...state,
				isCreatingDowngradeCheckout: false,
				isPerformingAction: false,
				downgradeCheckoutError: null,
			};

		case CREATE_DOWNGRADE_CHECKOUT_ERROR:
			return {
				...state,
				isCreatingDowngradeCheckout: false,
				isPerformingAction: false,
				downgradeCheckoutError: action.payload,
			};

		// Stripe Portal Session
		case CREATE_PORTAL_SESSION_REQUEST:
			return {
				...state,
				isCreatingPortalSession: true,
				isPerformingAction: true,
				portalSessionError: null,
			};

		case CREATE_PORTAL_SESSION_SUCCESS:
			return {
				...state,
				isCreatingPortalSession: false,
				isPerformingAction: false,
				portalSessionError: null,
			};

		case CREATE_PORTAL_SESSION_ERROR:
			return {
				...state,
				isCreatingPortalSession: false,
				isPerformingAction: false,
				portalSessionError: action.payload,
			};

		// Stripe Checkout - Subscribe
		case CREATE_SUBSCRIBE_CHECKOUT_REQUEST:
			return {
				...state,
				isCreatingSubscribeCheckout: true,
				isPerformingAction: true,
				subscribeCheckoutError: null,
			};

		case CREATE_SUBSCRIBE_CHECKOUT_SUCCESS:
			return {
				...state,
				isCreatingSubscribeCheckout: false,
				isPerformingAction: false,
				subscribeCheckoutError: null,
			};

		case CREATE_SUBSCRIBE_CHECKOUT_ERROR:
			return {
				...state,
				isCreatingSubscribeCheckout: false,
				isPerformingAction: false,
				subscribeCheckoutError: action.payload,
			};

		// Cancel Flow - Check Limits
		case CHECK_CANCEL_LIMITS_REQUEST:
			return {
				...state,
				isCheckingLimits: true,
				isPerformingAction: true,
				limitsCheckError: null,
				cancelFlowData: null,
			};

		case CHECK_CANCEL_LIMITS_SUCCESS:
			return {
				...state,
				isCheckingLimits: false,
				isPerformingAction: false,
				limitsCheckError: null,
				cancelFlowData: action.payload,
			};

		case CHECK_CANCEL_LIMITS_ERROR:
			return {
				...state,
				isCheckingLimits: false,
				isPerformingAction: false,
				limitsCheckError: action.payload,
				cancelFlowData: null,
			};

		// Usage Summary
		case FETCH_USAGE_SUMMARY_REQUEST:
			return {
				...state,
				isLoadingUsageSummary: true,
				usageSummaryError: null,
			};

		case FETCH_USAGE_SUMMARY_SUCCESS:
			return {
				...state,
				isLoadingUsageSummary: false,
				usageSummary: action.payload,
				usageSummaryError: null,
			};

		case FETCH_USAGE_SUMMARY_ERROR:
			return {
				...state,
				isLoadingUsageSummary: false,
				usageSummaryError: action.payload,
			};

		// SMS Add-on
		case UPDATE_SMS_ADDON_REQUEST:
			return {
				...state,
				isUpdatingSmsAddon: true,
				smsAddonError: null,
			};

		case UPDATE_SMS_ADDON_SUCCESS:
			return {
				...state,
				isUpdatingSmsAddon: false,
				smsAddonError: null,
			};

		case UPDATE_SMS_ADDON_ERROR:
			return {
				...state,
				isUpdatingSmsAddon: false,
				smsAddonError: action.payload,
			};

		// Clear Errors
		case CLEAR_SUBSCRIPTION_ERRORS:
			return {
				...state,
				currentSubscriptionError: null,
				paymentHistoryError: null,
				availablePlansError: null,
				upgradeError: null,
				downgradeError: null,
				pauseError: null,
				resumeError: null,
				cancelError: null,
				limitsCheckError: null,
				upgradeCheckoutError: null,
				downgradeCheckoutError: null,
				portalSessionError: null,
				subscribeCheckoutError: null,
				usageSummaryError: null,
			};

		default:
			return state;
	}
};

export default subscriptionReducer;
